import * as React from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  CalendarClock,
  Info,
  LogIn,
  MoreHorizontal,
  RefreshCw,
  Search,
  X,
  XCircle,
} from "lucide-react";
import {
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTablePagination,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  PageHeader,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
  toast,
  type StatusPillStatus,
} from "@eduviaos/ui-kit";
import { ApiError } from "@eduviaos/shared";
import { useCanManageBookings } from "@/hooks/useSeatingAuthoring";
import { usePageSizePreference } from "@/hooks/usePageSizePreference";
import { useStudentsList } from "@/hooks/useStudents";
import { CancelBookingDialog } from "@/components/seating/CancelBookingDialog";
import {
  useBookings,
  useCheckInBooking,
  type Booking,
  type BookingListFilters,
  type BookingStatus,
} from "@/lib/seating/bookings";
import { useSeats } from "@/lib/seating/seats";
import { useZones } from "@/lib/seating/zones";
import { useSeatConfig } from "@/lib/seatAssignmentConfig/api";
import { describeSlotKey, useSeatSlots } from "@/lib/seatSlots/api";

/**
 * Phase F-finale-7 Slice 7b.4d — IA booking-admin surface.
 *
 * The per-session booking view (Iron Rule #16 `<DataTable>`) over the
 * real-auth `/api/bookings` surface: each row is a (member, seat/zone, slot)
 * booking with its lifecycle status. Admins check-in held bookings and
 * cancel via `<CancelBookingDialog>` (reason-required). Booking *creation*
 * is a student-facing action (`book_seat`) and is intentionally not exposed
 * here.
 *
 * Bookings only exist when the tenant's `seat_assignment_config.mode` is
 * `dynamic` (seat-level) or `zone_only` (zone-level); `fixed` uses permanent
 * allocations and `none` disables platform seat tracking. When the mode
 * isn't a booking mode we surface an inline notice pointing to the right
 * surface instead of an empty table.
 *
 * Write affordances + the list read gate on `useCanManageBookings`
 * (`manage_bookings`), matching the BE `_BOOKING_WRITE_GUARD`; the BE
 * re-checks on every call.
 */

const ANY_ZONE = "__any__";
const ANY_STATUS = "__any__";

const STATUS_FILTER_OPTIONS = [
  { value: "held", label: "Held" },
  { value: "checked_in", label: "Checked in" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "no_show", label: "No-show" },
  { value: ANY_STATUS, label: "Any status" },
] as const;

type StatusFilter = (typeof STATUS_FILTER_OPTIONS)[number]["value"];

/** Booking status → the shared `<StatusPill>` palette + label. */
const STATUS_PILL: Record<string, { status: StatusPillStatus; label: string }> = {
  held: { status: "invited", label: "Held" },
  checked_in: { status: "active", label: "Checked in" },
  completed: { status: "inactive", label: "Completed" },
  cancelled: { status: "suspended", label: "Cancelled" },
  no_show: { status: "archived", label: "No-show" },
};

type DialogState = { kind: "none" } | { kind: "cancel"; booking: Booking };

function formatDateKey(dateKey: string): string {
  // `date_key` is a plain `YYYY-MM-DD`; parse as local calendar date (not
  // UTC midnight) so the displayed day never shifts by a timezone offset.
  const parts = dateKey.split("-").map(Number);
  if (parts.length === 3 && parts.every((n) => !Number.isNaN(n))) {
    const [y, m, d] = parts;
    const dt = new Date(y!, m! - 1, d!);
    return dt.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
  }
  return dateKey;
}

export function BookingsPage() {
  const { canManageBookings, missingRbac } = useCanManageBookings();

  const [qInput, setQInput] = React.useState("");
  const [zoneFilter, setZoneFilter] = React.useState<string>(ANY_ZONE);
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>(ANY_STATUS);
  const [dateFilter, setDateFilter] = React.useState<string>("");
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = usePageSizePreference({
    scope: "seating-bookings",
    defaultPageSize: 10,
  });
  const [dialog, setDialog] = React.useState<DialogState>({ kind: "none" });

  const configQuery = useSeatConfig();
  const mode = configQuery.data?.mode;
  const isBookingMode = mode === "dynamic" || mode === "zone_only";

  // Zones + seats + students power the filters and the row-level name
  // resolution (bookings carry only ids on the wire).
  const zonesQuery = useZones({ limit: 200 });
  const zones = React.useMemo(() => zonesQuery.data?.items ?? [], [zonesQuery.data]);
  const zoneNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const z of zones) map.set(z.id, z.name);
    return map;
  }, [zones]);

  const seatsQuery = useSeats({ limit: 200 });
  const seatById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const s of seatsQuery.data?.items ?? []) map.set(s.id, s.seat_number);
    return map;
  }, [seatsQuery.data]);

  const studentsQuery = useStudentsList({ limit: 200 });
  const studentNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const s of studentsQuery.data?.items ?? []) map.set(s.id, s.name);
    return map;
  }, [studentsQuery.data]);

  const filters = React.useMemo<BookingListFilters>(
    () => ({
      zone_id: zoneFilter === ANY_ZONE ? undefined : zoneFilter,
      status: statusFilter === ANY_STATUS ? undefined : (statusFilter as BookingStatus),
      date_key: dateFilter || undefined,
      limit: pageSize,
      offset: page * pageSize,
    }),
    [zoneFilter, statusFilter, dateFilter, page, pageSize],
  );

  // The BE booking read guard is `manage_bookings|book_seat`; a principal
  // without `manage_bookings` would 403, so only fire the query when the
  // admin actually holds it (the nav gate matches).
  const query = useBookings(filters, { enabled: canManageBookings });
  const { data, isLoading, isFetching, isError, error, refetch } = query;

  const total = data?.total ?? 0;
  const allItems = React.useMemo(() => data?.items ?? [], [data]);

  const slotsQuery = useSeatSlots();
  const slots = React.useMemo(() => slotsQuery.data?.items ?? [], [slotsQuery.data]);
  const slotName = React.useCallback((b: Booking) => describeSlotKey(b.slot_key, slots), [slots]);

  const seatLabel = React.useCallback(
    (b: Booking) => (b.seat_id ? (seatById.get(b.seat_id) ?? b.seat_id) : null),
    [seatById],
  );
  const studentLabel = React.useCallback(
    (b: Booking) => studentNameById.get(b.student_id) ?? b.student_id,
    [studentNameById],
  );

  const filteredItems = React.useMemo(() => {
    const needle = qInput.trim().toLowerCase();
    if (!needle) return allItems;
    return allItems.filter((b) =>
      [
        studentLabel(b),
        seatLabel(b) ?? "",
        zoneNameById.get(b.zone_id ?? "") ?? "",
        b.slot_key,
        slotName(b).label,
      ]
        .join(" ")
        .toLowerCase()
        .includes(needle),
    );
  }, [allItems, qInput, seatLabel, slotName, studentLabel, zoneNameById]);

  const hasFiltersApplied =
    qInput !== "" || zoneFilter !== ANY_ZONE || statusFilter !== ANY_STATUS || dateFilter !== "";

  const clearFilters = React.useCallback(() => {
    setQInput("");
    setZoneFilter(ANY_ZONE);
    setStatusFilter(ANY_STATUS);
    setDateFilter("");
    setPage(0);
  }, []);

  const checkInMutation = useCheckInBooking();
  const handleCheckIn = React.useCallback(
    async (b: Booking) => {
      try {
        await checkInMutation.mutateAsync({ id: b.id });
        toast.success(`Checked in ${studentNameById.get(b.student_id) ?? "learner"}.`);
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message || "Couldn't check in the booking. Please retry."
            : err instanceof Error
              ? err.message
              : "Couldn't check in the booking. Please retry.";
        toast.error(message);
      }
    },
    [checkInMutation, studentNameById],
  );

  return (
    <div className="space-y-6">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Bookings</PageHeaderHeading>
            <PageHeaderDescription>
              Per-session seat holds. Check learners in when they arrive and cancel bookings that
              won&apos;t be used.
            </PageHeaderDescription>
          </PageHeaderTitle>
        </PageHeaderTop>
      </PageHeader>

      {missingRbac ? (
        <Card data-testid="bookings-missing-rbac-notice" className="border-amber-300/60">
          <CardHeader>
            <CardTitle className="text-base">View-only access</CardTitle>
            <CardDescription>
              You don&apos;t have the <span className="font-medium">Manage bookings</span>{" "}
              permission, so this surface is unavailable. Ask an administrator to grant it if you
              need to check learners in or cancel bookings.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      {!missingRbac && configQuery.isSuccess && !isBookingMode ? (
        <Card data-testid="bookings-mode-notice" className="border-sky-300/60">
          <CardHeader>
            <div className="flex items-start gap-2">
              <Info aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-sky-600" />
              <div className="space-y-1">
                <CardTitle className="text-base">Bookings need dynamic or zone-only mode</CardTitle>
                <CardDescription>
                  {mode === "fixed" ? (
                    <>
                      Your seating mode is <span className="font-medium">Fixed</span>, which uses
                      permanent{" "}
                      <Link
                        to="/seating/allocations"
                        className="text-primary font-medium underline"
                      >
                        allocations
                      </Link>{" "}
                      instead of per-session bookings.
                    </>
                  ) : (
                    <>
                      Your seating mode doesn&apos;t track per-session bookings.{" "}
                      <Link to="/seating/config" className="text-primary font-medium underline">
                        Switch to dynamic or zone-only mode
                      </Link>{" "}
                      to let learners book seats.
                    </>
                  )}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      ) : null}

      {isError ? (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">
            Couldn&apos;t load bookings
            {error instanceof Error && error.message ? `: ${error.message}` : "."}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-0.5 text-xs"
            onClick={() => refetch()}
          >
            Retry
          </Button>
        </div>
      ) : null}

      <DataTable responsive="cards">
        <DataTableToolbar>
          <div className="relative w-full sm:w-64">
            <Search
              aria-hidden="true"
              className="text-muted-foreground absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2"
            />
            <Input
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Search learner, seat or slot…"
              className="pl-8"
              aria-label="Search bookings"
            />
          </div>
          <Input
            type="date"
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value);
              setPage(0);
            }}
            className="h-9 w-40"
            aria-label="Date filter"
          />
          <Select
            value={zoneFilter}
            onValueChange={(v) => {
              setZoneFilter(v);
              setPage(0);
            }}
          >
            <SelectTrigger className="h-9 w-44" aria-label="Zone filter">
              <SelectValue placeholder="Any zone" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_ZONE}>Any zone</SelectItem>
              {zones.map((z) => (
                <SelectItem key={z.id} value={z.id}>
                  {z.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v as StatusFilter);
              setPage(0);
            }}
          >
            <SelectTrigger className="h-9 w-36" aria-label="Status filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hasFiltersApplied ? (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X aria-hidden="true" className="size-3.5" />
              Clear
            </Button>
          ) : null}
          <DataTableToolbarSpacer />
          <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw aria-hidden="true" className={cn("size-4", isFetching && "animate-spin")} />
            Refresh
          </Button>
          <span className="text-muted-foreground text-xs tabular-nums">
            {total.toLocaleString()} total
          </span>
        </DataTableToolbar>

        <DataTableScroll>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[26%]">Learner</TableHead>
                <TableHead className="w-[22%]">Seat / zone</TableHead>
                <TableHead className="w-[14%]">Slot</TableHead>
                <TableHead className="w-[16%]">Date</TableHead>
                <TableHead className="w-[14%]">Status</TableHead>
                <TableHead className="w-[8%] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            {isLoading ? (
              <DataTableSkeleton rows={5} columns={6} />
            ) : (
              <TableBody>
                {filteredItems.map((b) => {
                  const pill = STATUS_PILL[b.status] ?? { status: "inactive", label: b.status };
                  const zoneName = b.zone_id ? zoneNameById.get(b.zone_id) : undefined;
                  const seat = seatLabel(b);
                  const slot = slotName(b);
                  const canCheckIn = b.status === "held";
                  const canCancel = b.status === "held" || b.status === "checked_in";
                  const hasActions = canManageBookings && (canCheckIn || canCancel);
                  return (
                    <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                      <TableCell className="font-medium">{studentLabel(b)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {seat ?? (zoneName ? "—" : "—")}
                        {zoneName ? (
                          <span className="text-muted-foreground/70">
                            {seat ? " · " : ""}
                            {zoneName}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <span className="block" title={b.slot_key}>
                          {slot.label}
                        </span>
                        {slot.hours ? (
                          <span className="text-muted-foreground block text-xs tabular-nums">
                            {slot.hours}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-muted-foreground tabular-nums">
                        <span title={b.date_key}>{formatDateKey(b.date_key)}</span>
                      </TableCell>
                      <TableCell>
                        <StatusPill status={pill.status} label={pill.label} />
                      </TableCell>
                      <TableCell className="text-right">
                        {hasActions ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`Actions for ${studentLabel(b)}`}
                                data-testid={`booking-row-actions-${b.id}`}
                                className="size-7"
                              >
                                <MoreHorizontal aria-hidden="true" className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44">
                              {canCheckIn ? (
                                <DropdownMenuItem
                                  onSelect={() => void handleCheckIn(b)}
                                  data-testid={`booking-action-checkin-${b.id}`}
                                >
                                  <LogIn aria-hidden="true" className="size-4" />
                                  Check in
                                </DropdownMenuItem>
                              ) : null}
                              {canCheckIn && canCancel ? <DropdownMenuSeparator /> : null}
                              {canCancel ? (
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onSelect={() => setDialog({ kind: "cancel", booking: b })}
                                  data-testid={`booking-action-cancel-${b.id}`}
                                >
                                  <XCircle aria-hidden="true" className="size-4" />
                                  Cancel booking
                                </DropdownMenuItem>
                              ) : null}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>

        {!isLoading && filteredItems.length === 0 ? (
          <DataTableEmpty
            icon={<CalendarClock aria-hidden="true" />}
            title={hasFiltersApplied ? "No matches" : "No bookings yet"}
            description={
              hasFiltersApplied
                ? "Adjust filters or clear them to see every booking."
                : isBookingMode
                  ? "Bookings appear here once learners start reserving seats."
                  : "Per-session bookings appear here once seating is in a booking mode."
            }
            action={
              hasFiltersApplied ? (
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  <X aria-hidden="true" className="size-3.5" />
                  Clear filters
                </Button>
              ) : null
            }
          />
        ) : null}

        {!isLoading && filteredItems.length > 0 ? (
          <DataTableFooter>
            <span className="text-muted-foreground text-sm tabular-nums">
              Showing {filteredItems.length.toLocaleString()} of {total.toLocaleString()}
            </span>
            <DataTablePagination
              className="ml-auto"
              page={page + 1}
              pageSize={pageSize}
              totalRows={total}
              isFetching={isFetching}
              onPageChange={(next) => setPage(Math.max(0, next - 1))}
              onPageSizeChange={(next) => {
                setPageSize(next);
                setPage(0);
              }}
              testIdPrefix="bookings-pagination"
            />
          </DataTableFooter>
        ) : null}
      </DataTable>

      {canManageBookings ? (
        <CancelBookingDialog
          open={dialog.kind === "cancel"}
          onOpenChange={(next) => {
            if (!next) setDialog({ kind: "none" });
          }}
          booking={dialog.kind === "cancel" ? dialog.booking : null}
          seatLabel={
            dialog.kind === "cancel" ? (seatLabel(dialog.booking) ?? undefined) : undefined
          }
          studentLabel={dialog.kind === "cancel" ? studentLabel(dialog.booking) : undefined}
          onCancelled={() => setDialog({ kind: "none" })}
        />
      ) : null}
    </div>
  );
}
